import { useEffect } from "react";
import "./inspection-project-folders.css";

type FolderRow = {
  project: string;
  record: string;
  type: string;
  drawingWeld: string;
  stage: string;
  method: string;
  inspector: string;
  date: string;
  result: string;
};

const clean = (value: string | null | undefined) => (value || "").replace(/\s+/g, " ").trim();

function readRows(table: HTMLTableElement): FolderRow[] {
  return Array.from(table.querySelectorAll<HTMLTableRowElement>("tbody tr")).map((row) => {
    const cells = Array.from(row.cells);
    const first = cells[0];
    const projectCell = cells[1];
    return {
      project: clean(projectCell?.querySelector("b")?.textContent) || "Unlinked records",
      record: clean(first?.querySelector("b")?.textContent) || "Inspection record",
      type: clean(first?.querySelector(".record-type")?.textContent) || "INSPECTION",
      drawingWeld: clean(projectCell?.querySelector("small")?.textContent) || "No drawing or weld linked",
      stage: clean(cells[2]?.textContent) || "—",
      method: clean(cells[3]?.textContent) || "—",
      inspector: clean(cells[4]?.textContent) || "—",
      date: clean(cells[5]?.textContent) || "—",
      result: clean(cells[6]?.textContent) || "—",
    };
  });
}

function iconFor(type: string) {
  const upper = type.toUpperCase();
  if (upper.includes("NDT")) return "NDT";
  if (upper.includes("HOLD")) return "HOLD";
  if (upper.includes("ITP")) return "ITP";
  return "INSP";
}

export default function InspectionProjectFolders() {
  useEffect(() => {
    const openProjects = new Set<string>();
    let queued = false;

    const rebuild = () => {
      queued = false;
      const table = document.querySelector<HTMLTableElement>(".inspection-table");
      if (!table) return;
      const card = table.closest(".table-card");
      if (!card) return;

      table.closest<HTMLElement>(".table-scroll")?.classList.add("inspection-source-table");
      let host = card.querySelector<HTMLElement>(".inspection-project-folders");
      if (!host) {
        host = document.createElement("div");
        host.className = "inspection-project-folders";
        table.closest(".table-scroll")?.before(host);
      }

      const rows = readRows(table);
      const grouped = new Map<string, FolderRow[]>();
      rows.forEach((row) => grouped.set(row.project, [...(grouped.get(row.project) || []), row]));

      host.innerHTML = "";
      if (!rows.length) {
        const empty = document.createElement("div");
        empty.className = "inspection-folder-empty";
        empty.textContent = "No inspection records match the current filter.";
        host.appendChild(empty);
        return;
      }

      Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b)).forEach(([project, records]) => {
        const folder = document.createElement("section");
        folder.className = "inspection-job-folder";
        const expanded = openProjects.has(project);
        if (expanded) folder.classList.add("open");

        const ndt = records.filter((r) => r.type.toUpperCase().includes("NDT")).length;
        const holds = records.filter((r) => r.type.toUpperCase().includes("HOLD")).length;
        const rejected = records.filter((r) => r.result.toUpperCase().includes("REJECTED")).length;

        const head = document.createElement("button");
        head.type = "button";
        head.className = "inspection-job-folder-head";
        head.innerHTML = `<span class="inspection-folder-chevron">${expanded ? "▾" : "▸"}</span><span class="inspection-folder-title"><small>PROJECT INSPECTION FILE</small><strong>${project}</strong></span><span class="inspection-folder-count">${records.length} record${records.length === 1 ? "" : "s"}</span><span class="inspection-folder-stat">${ndt} NDT</span>${holds ? `<span class="inspection-folder-stat warning">${holds} hold</span>` : ""}${rejected ? `<span class="inspection-folder-stat danger">${rejected} rejected</span>` : ""}`;
        head.addEventListener("click", () => {
          if (openProjects.has(project)) openProjects.delete(project); else openProjects.add(project);
          rebuild();
        });
        folder.appendChild(head);

        const body = document.createElement("div");
        body.className = "inspection-job-folder-body record-list";
        records.forEach((record) => {
          const article = document.createElement("article");
          article.className = "inspection-folder-record";
          article.innerHTML = `<span class="record-icon">${iconFor(record.type)}</span><div class="inspection-folder-primary"><b>${record.record}</b><small>${record.drawingWeld}</small></div><div><b>${record.method}</b><small>${record.stage}</small></div><div><b>${record.inspector}</b><small>${record.date}</small></div><span class="status ${record.result.toUpperCase().includes("REJECTED") ? "expired" : record.result.toUpperCase().includes("PENDING") ? "expiring" : "active"}">● ${record.result}</span>`;
          body.appendChild(article);
        });
        folder.appendChild(body);
        host!.appendChild(folder);
      });
    };

    const schedule = (mutations?: MutationRecord[]) => {
      if (mutations?.length && mutations.every((m) => (m.target as Element).closest?.(".inspection-project-folders"))) return;
      if (queued) return;
      queued = true;
      requestAnimationFrame(rebuild);
    };
    const observer = new MutationObserver((mutations) => schedule(mutations));
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    const refresh = () => schedule();
    window.addEventListener("arcvault-inspection-refresh", refresh);
    rebuild();
    return () => {
      observer.disconnect();
      window.removeEventListener("arcvault-inspection-refresh", refresh);
      document.querySelector(".inspection-project-folders")?.remove();
      document.querySelector(".inspection-source-table")?.classList.remove("inspection-source-table");
    };
  }, []);
  return null;
}
